import { useState } from 'react';

const LS_KEY = 'wm-integrations-v1';

function loadIntegrations() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return { quickbooks: null, xero: null, autoSyncPaid: false, includeLineItems: true, lastSync: null };
}

function saveIntegrations(data) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(data)); } catch { /* ignore */ }
}

function OAuthModal({ provider, onClose }) {
  const labels = { quickbooks: 'QuickBooks Online', xero: 'Xero' };
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-2xl shadow-2xl bg-white dark:bg-[#1B2A3E] border border-gray-200 dark:border-[#243348] p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[var(--wm-text-heading)] font-bold text-[#0F1923] dark:text-[#F8FAFE]">Connect {labels[provider]}</h3>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-md text-[#64748B] hover:bg-gray-100 dark:hover:bg-[#243348]">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
          </button>
        </div>
        <p className="text-[var(--wm-text-body)] text-[#64748B] dark:text-[#7D93AE] mb-4">
          To connect {labels[provider]}, you need a {labels[provider]} developer account. Click below to start the OAuth flow.
        </p>
        <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 mb-4">
          <p className="text-[var(--wm-text-ui)] text-amber-700 dark:text-amber-400">
            Configure your {labels[provider]} App Client ID in Vercel environment variables first.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 py-2 rounded-lg text-[var(--wm-text-body)] font-medium bg-gray-100 dark:bg-[#243348] text-[#0F1923] dark:text-[#F8FAFE] hover:bg-gray-200 dark:hover:bg-[#2A3D55] transition-colors"
          >
            Cancel
          </button>
          <button
            disabled
            className="flex-1 py-2 rounded-lg text-[var(--wm-text-body)] font-semibold bg-[#2E8BF0]/30 text-white cursor-not-allowed"
            title="Configure env vars first"
          >
            Start OAuth Flow
          </button>
        </div>
      </div>
    </div>
  );
}

function IntegrationCard({ id, name, description, logo, integration, onConnect, onDisconnect, onSync }) {
  const connected = integration?.connected;
  return (
    <div className="p-4 rounded-xl border border-gray-200 dark:border-[#243348] bg-white dark:bg-[#1B2A3E]">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-[#243348] flex items-center justify-center flex-shrink-0 text-[var(--wm-text-heading)] font-bold text-[#64748B]">
          {logo}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <h3 className="text-[var(--wm-text-body)] font-semibold text-[#0F1923] dark:text-[#F8FAFE]">{name}</h3>
            <span className={`text-[var(--wm-text-caption)] font-semibold px-1.5 py-0.5 rounded-full ${
              connected
                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                : 'bg-gray-100 text-[#64748B] dark:bg-[#243348] dark:text-[#7D93AE]'
            }`}>
              {connected ? 'Connected' : 'Not connected'}
            </span>
          </div>
          <p className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE]">{description}</p>
          {connected && integration.companyName && (
            <p className="text-[var(--wm-text-ui)] text-[#2E8BF0] dark:text-[#60ABFF] mt-1 font-medium">{integration.companyName}</p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {connected ? (
            <>
              <button
                onClick={() => onSync(id)}
                className="px-3 py-1.5 rounded-lg text-[var(--wm-text-ui)] font-semibold bg-[#2E8BF0]/10 text-[#2E8BF0] dark:text-[#60ABFF] hover:bg-[#2E8BF0]/20 transition-colors"
              >
                Sync Now
              </button>
              <button
                onClick={() => onDisconnect(id)}
                className="px-3 py-1.5 rounded-lg text-[var(--wm-text-ui)] font-semibold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                Disconnect
              </button>
            </>
          ) : (
            <button
              onClick={() => onConnect(id)}
              className="px-3 py-1.5 rounded-lg text-[var(--wm-text-ui)] font-semibold bg-[#2E8BF0] text-white hover:bg-[#2578D4] transition-colors"
            >
              Connect {name.split(' ')[0]}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function IntegrationsTab() {
  const [data, setData] = useState(loadIntegrations);
  const [oauthModal, setOauthModal] = useState(null); // 'quickbooks' | 'xero' | null

  const save = (next) => {
    setData(next);
    saveIntegrations(next);
  };

  const handleConnect = (id) => {
    setOauthModal(id);
  };

  const handleDisconnect = (id) => {
    save({ ...data, [id]: null });
  };

  const handleSync = (_id) => {
    save({ ...data, lastSync: new Date().toISOString() });
  };

  const handleOAuthClose = () => {
    // Stub: simulate "connected" for demo purposes — real impl would exchange token
    // For now just close the modal
    setOauthModal(null);
  };

  const anyConnected = data.quickbooks?.connected || data.xero?.connected;

  const integrations = [
    {
      id: 'quickbooks',
      name: 'QuickBooks Online',
      description: 'Sync invoices, payments, and customers with QuickBooks Online.',
      logo: 'QB',
    },
    {
      id: 'xero',
      name: 'Xero',
      description: 'Push invoices and contacts to Xero automatically.',
      logo: 'X',
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-[var(--wm-text-body)] font-bold text-[#0F1923] dark:text-[#F8FAFE] mb-1">Accounting Integrations</h2>
        <p className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE]">Connect WrapMind to your accounting software to sync invoices and payments.</p>
      </div>

      <div className="space-y-3">
        {integrations.map((intg) => (
          <IntegrationCard
            key={intg.id}
            {...intg}
            integration={data[intg.id]}
            onConnect={handleConnect}
            onDisconnect={handleDisconnect}
            onSync={handleSync}
          />
        ))}
      </div>

      {anyConnected && (
        <div className="p-4 rounded-xl border border-gray-200 dark:border-[#243348] bg-white dark:bg-[#1B2A3E] space-y-4">
          <h3 className="text-[var(--wm-text-body)] font-semibold text-[#0F1923] dark:text-[#F8FAFE]">Sync Settings</h3>

          <label className="flex items-center justify-between gap-4 cursor-pointer">
            <div>
              <p className="text-[var(--wm-text-body)] text-[#0F1923] dark:text-[#F8FAFE]">Auto-sync invoices when marked paid</p>
              <p className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE]">Automatically push paid invoices to connected accounting software.</p>
            </div>
            <button
              role="switch"
              aria-checked={data.autoSyncPaid}
              onClick={() => save({ ...data, autoSyncPaid: !data.autoSyncPaid })}
              className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors duration-200 ${
                data.autoSyncPaid ? 'bg-[#2E8BF0]' : 'bg-gray-300 dark:bg-[#243348]'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 mt-0.5 ${
                data.autoSyncPaid ? 'translate-x-4' : 'translate-x-0.5'
              }`} />
            </button>
          </label>

          <label className="flex items-center justify-between gap-4 cursor-pointer">
            <div>
              <p className="text-[var(--wm-text-body)] text-[#0F1923] dark:text-[#F8FAFE]">Include line items</p>
              <p className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE]">Send individual line items instead of a single total.</p>
            </div>
            <button
              role="switch"
              aria-checked={data.includeLineItems}
              onClick={() => save({ ...data, includeLineItems: !data.includeLineItems })}
              className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors duration-200 ${
                data.includeLineItems ? 'bg-[#2E8BF0]' : 'bg-gray-300 dark:bg-[#243348]'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 mt-0.5 ${
                data.includeLineItems ? 'translate-x-4' : 'translate-x-0.5'
              }`} />
            </button>
          </label>

          {data.lastSync && (
            <p className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE]">
              Last sync: {new Date(data.lastSync).toLocaleString()}
            </p>
          )}
        </div>
      )}

      {oauthModal && (
        <OAuthModal provider={oauthModal} onClose={handleOAuthClose} />
      )}
    </div>
  );
}
